import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PremiumTable from '../components/common/PremiumTable';
import PageHeader from '../components/common/PageHeader';
import { 
  ShieldCheckIcon, 
  UserMinusIcon, 
  UserPlusIcon, 
  CreditCardIcon, 
  CogIcon, 
  MegaphoneIcon,
  ExclamationCircleIcon,
  MapIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Map Modal State
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getAuditLogs();
      setLogs(res.data.data || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'USER_BLOCKED': return <UserMinusIcon className="h-5 w-5 text-red-500" />;
      case 'USER_UNBLOCKED': return <UserPlusIcon className="h-5 w-5 text-green-500" />;
      case 'PLAN_UPDATED': return <CreditCardIcon className="h-5 w-5 text-purple-500" />;
      case 'BUSINESS_SUSPENDED': return <ShieldCheckIcon className="h-5 w-5 text-red-600" />;
      case 'SETTING_UPDATED': return <CogIcon className="h-5 w-5 text-blue-500" />;
      case 'BROADCAST_SENT': return <MegaphoneIcon className="h-5 w-5 text-yellow-500" />;
      default: return <ExclamationCircleIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const formatActionName = (action) => {
    if (!action) return 'UNKNOWN';
    return action.replace(/_/g, ' ');
  };

  const columns = [
    {
      header: 'Action',
      accessor: 'action',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 shadow-sm">
            {getActionIcon(row.action)}
          </div>
          <div>
            <span className="text-sm font-bold text-gray-900">{formatActionName(row.action)}</span>
          </div>
        </div>
      )
    },
    {
      header: 'Admin / Initiator',
      accessor: 'adminId.name',
      render: (row) => (
        row.adminId ? (
          <div>
            <div className="text-sm font-medium text-gray-900">{row.adminId.name}</div>
            <div className="text-xs text-gray-500">{row.adminId.email}</div>
          </div>
        ) : (
          <span className="text-sm text-gray-500">System</span>
        )
      )
    },
    {
      header: 'Details',
      accessor: 'details.description',
      render: (row) => {
        let text = 'N/A';
        if (row.details && row.details.description) {
          text = row.details.description;
        } else if (row.details) {
          try {
            const keys = Object.keys(row.details);
            if (keys.length > 0) {
              text = `Updated: ${keys.join(', ')}`;
            }
          } catch(e) {}
        }
        return (
          <div className="max-w-md truncate text-sm text-gray-600" title={typeof row.details === 'object' ? JSON.stringify(row.details) : text}>
            {text}
          </div>
        );
      }
    },
    {
      header: 'IP & Location',
      accessor: 'ipAddress',
      render: (row) => (
        <div className="flex flex-col items-start gap-1">
          <span className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded">
            {row.ipAddress || '0.0.0.0'}
          </span>
          {row.location && (
            <div className="flex items-center gap-2 mt-0.5">
              <div className="text-[10px] text-gray-500">
                <span className="font-medium text-gray-700 block">{row.location.city}, {row.location.country}</span>
                <span>{row.location.lat}, {row.location.lon}</span>
              </div>
              <button 
                onClick={() => { setSelectedLog(row); setIsMapModalOpen(true); }}
                className="p-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                title="View on Map"
              >
                <MapIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Date & Time',
      accessor: 'createdAt',
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {new Date(row.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(row.createdAt).toLocaleTimeString()}
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="Audit Logs"
        subtitle="Track administrator actions and critical system events for security and compliance."
      />

      <main className="max-w-7xl">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading audit logs...</div>
        ) : (
          <PremiumTable 
            title="System Activity"
            data={logs}
            columns={columns}
            searchKeys={['action', 'adminId.name', 'adminId.email', 'ipAddress']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={15}
          />
        )}
      </main>

      {/* Map Modal */}
      {isMapModalOpen && selectedLog && selectedLog.location && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-50 rounded-lg">
                  <MapIcon className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Location Details</h3>
                  <p className="text-xs text-gray-500">IP Address: <span className="font-mono">{selectedLog.ipAddress}</span></p>
                </div>
              </div>
              <button 
                onClick={() => setIsMapModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">City</span>
                  <span className="text-sm font-medium text-gray-900">{selectedLog.location.city || 'N/A'}</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Country</span>
                  <span className="text-sm font-medium text-gray-900">{selectedLog.location.country || 'N/A'}</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Latitude</span>
                  <span className="text-sm font-medium text-gray-900 font-mono">{selectedLog.location.lat}</span>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <span className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">Longitude</span>
                  <span className="text-sm font-medium text-gray-900 font-mono">{selectedLog.location.lon}</span>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                <iframe
                  width="100%"
                  height="300"
                  frameBorder="0"
                  scrolling="no"
                  marginHeight="0"
                  marginWidth="0"
                  src={`https://maps.google.com/maps?q=${selectedLog.location.lat},${selectedLog.location.lon}&z=13&output=embed`}
                  title="Location Map"
                  className="w-full"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;
